'use client';

import LinkRouter from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { ProductTypes } from '@bytel/magicart';
import { FUNDING_ENUM } from '@bytel/magicart/dist/src/interfaces';
import { Button, ButtonVariant, Column, Icon, IconName, Link, Text } from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull } from '@app-types/product';
import { UserType } from '@app-types/user';

import { getCart } from '@helpers/cart';
import { getSessionFunding } from '@helpers/funding';
import { paths } from '@helpers/path';
import { getNeededElementGencodes, getSimilarProductsLink } from '@helpers/product';

import { PlanRange } from '@constants/plan';
import { TradeInStep } from '@constants/trade-in';

import { clearSessionTradeInProduct, getSessionTradeInLastStep, isOutOfStock } from '@product-detail/helpers';
import { ClickEvent, OnClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

export type ProductCTAProps = {
    type: WallType;
    fullwidth?: boolean;
    mobileIcon?: boolean;
    openShopAvailabilityModal: ClickEvent;
    currentProduct: ProductChildFull;
    plan?: MobilePlanType;
    contract?: ContractType;
    user?: UserType;
    isRenewal: boolean;
    renewalRedirectWall?: boolean;
};

export function ProductCTA({
    type,
    fullwidth = false,
    mobileIcon = false,
    openShopAvailabilityModal,
    currentProduct,
    plan,
    contract,
    user,
    isRenewal,
    renewalRedirectWall,
}: ProductCTAProps) {
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();

    const {
        stockDetails: { stock, isAvailableInStore },
    } = currentProduct;

    const handleClick = async (event: OnClickEvent) => {
        if (renewalRedirectWall) {
            router.push(paths.phoneWall);
            return;
        }
        if (isOutOfStock(stock.status)) {
            openShopAvailabilityModal(event);
        } else {
            setIsLoading(true);
            const cart = await getCart(user, contract?.id);
            if (!plan) {
                await cart.removeProductsByType(ProductTypes._plan);
            }

            if (
                getSessionTradeInLastStep() !== TradeInStep.SEARCH &&
                getSessionTradeInLastStep() !== TradeInStep.CONFIRMED
            ) {
                clearSessionTradeInProduct();
            }

            const autoAdd = getNeededElementGencodes(plan);
            cart.addProductsToCart(
                plan ? [currentProduct.gencode, plan.gencode, ...autoAdd] : [currentProduct.gencode, ...autoAdd],
                false,
                isRenewal,
            )
                .then(async () => {
                    setIsLoading(false);
                    const funding = getSessionFunding();
                    if (funding) await cart.setFundingMethod(funding as unknown as FUNDING_ENUM);
                    if (type !== WallType.PHONE) {
                        router.push(paths.cart);
                    } else {
                        router.push(paths.crossSell('product-detail'));
                    }
                })
                .catch(() => {
                    router.push(paths.productWall(type));
                });
        }
    };

    if (user && contract && (plan ? plan.commercialRange === PlanRange.WITHOUT_MOBILE : contract.subscription.isSowo)) {
        return null;
    }

    return (
        <Column narrow={!fullwidth} className={fullwidth ? '' : 'is-paddingless'}>
            {isOutOfStock(stock.status) && !isAvailableInStore && (
                <Link href={getSimilarProductsLink(currentProduct, isRenewal)} routerLink={LinkRouter}>
                    <Button variant={ButtonVariant.CONVERSION} fullwidth={fullwidth} name={stock.cart}>
                        <ButtonContent wording={stock.cart} icon={stock.icon} hasMobileIcon={mobileIcon} />
                    </Button>
                </Link>
            )}
            {isOutOfStock(stock.status) && isAvailableInStore && (
                <Button
                    variant={ButtonVariant.CONVERSION}
                    fullwidth={fullwidth}
                    onClick={handleClick}
                    loading={isLoading}
                    name={stock.cart}
                >
                    <ButtonContent
                        wording='Voir les disponibilités en boutique'
                        icon={stock.icon}
                        hasMobileIcon={mobileIcon}
                    />
                </Button>
            )}
            {!isOutOfStock(stock.status) && (
                <Button
                    variant={ButtonVariant.CONVERSION}
                    fullwidth={fullwidth}
                    onClick={handleClick}
                    loading={isLoading}
                    name={stock.cart}
                    data-cy='product-add-to-cart'
                >
                    <ButtonContent wording={stock.cart} icon={stock.icon} hasMobileIcon={mobileIcon} />
                </Button>
            )}
        </Column>
    );
}

function ButtonContent({ wording, icon, hasMobileIcon }: { wording: string; icon: IconName; hasMobileIcon: boolean }) {
    return (
        <>
            <Text className={hasMobileIcon ? 'is-hidden-mobile' : ''} marginless>
                {wording}
            </Text>
            {hasMobileIcon && <Icon name={icon} className='is-hidden-tablet' />}
        </>
    );
}
